import { useEffect, useMemo, useState } from "react";

const COUNTRY_OPTIONS = [
    { code: "1", label: "US/CA (+1)" },
    { code: "44", label: "UK (+44)" },
    { code: "61", label: "AU (+61)" },
    { code: "52", label: "MX (+52)" },
];

function parseE164(value) {
    if (!value || typeof value !== "string") {
        return { countryCode: "1", national: "" };
    }

    const trimmed = value.trim();
    if (trimmed.startsWith("+")) {
        const digits = trimmed.slice(1).replace(/\D/g, "");
        for (const option of COUNTRY_OPTIONS) {
            if (digits.startsWith(option.code)) {
                return {
                    countryCode: option.code,
                    national: digits.slice(option.code.length),
                };
            }
        }
        return { countryCode: "1", national: digits };
    }

    return {
        countryCode: "1",
        national: trimmed.replace(/\D/g, ""),
    };
}

function formatNational(digits, countryCode) {
    if (countryCode === "1") {
        const d = digits.slice(0, 10);
        if (d.length <= 3) return d;
        if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }

    return digits;
}

function toE164(countryCode, nationalDigits) {
    const national = nationalDigits.replace(/\D/g, "");
    if (!national) return "";
    return `+${countryCode}${national}`;
}

/**
 * Collects a phone number and always emits E.164 (e.g. +15551234567) via onChange.
 */
export default function PhoneInput({
    id = "phone_number",
    name = "phone_number",
    value = "",
    onChange,
    disabled = false,
    className = "",
    placeholder = "(555) 123-4567",
}) {
    const initial = useMemo(() => parseE164(value), []);
    const [countryCode, setCountryCode] = useState(initial.countryCode);
    const [national, setNational] = useState(initial.national);

    useEffect(() => {
        const parsed = parseE164(value);
        setCountryCode(parsed.countryCode);
        setNational(parsed.national);
    }, [value]);

    const emit = (nextCountry, nextNational) => {
        const e164 = toE164(nextCountry, nextNational);
        onChange?.(e164);
    };

    return (
        <div className={`flex gap-2 ${className}`}>
            <select
                aria-label="Country code"
                value={countryCode}
                disabled={disabled}
                onChange={(e) => {
                    const next = e.target.value;
                    setCountryCode(next);
                    emit(next, national);
                }}
                className="w-36 shrink-0 rounded-lg border border-stroke bg-transparent py-4 pl-3 pr-2 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
            >
                {COUNTRY_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                        {option.label}
                    </option>
                ))}
            </select>
            <input
                id={id}
                name={name}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                disabled={disabled}
                value={formatNational(national, countryCode)}
                placeholder={placeholder}
                onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 15);
                    setNational(digits);
                    emit(countryCode, digits);
                }}
                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-4 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
            />
        </div>
    );
}
